import { inject, Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { ENVIRONMENT_CONFIG } from '@shared/injection-token/environment-config';
import { stringify } from 'qs';
import { EnvironmentConfig } from '@shared/interfaces/environment-config';

export enum RequestMethod {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

interface RequestInterface {
  method: RequestMethod;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export abstract class AbstractHttpService {
  protected readonly environmentConfig: EnvironmentConfig;
  protected url: string;
  protected readonly http: HttpClient;

  protected constructor(protected endpoint: string) {
    this.http = inject(HttpClient);
    this.environmentConfig = inject(ENVIRONMENT_CONFIG);
    this.url = this.environmentConfig.api.url;
  }

  protected get<T>(path: string, queryOptions: Record<string, unknown> = {}): Observable<T> {
    const queryParams = this.objectToQuerystring(queryOptions);
    const url = `${this.url}/${this.endpoint}${path ? `/${path}` : ''}${queryParams}`;
    return this.http.get<T>(url).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.GET, url }, response);
      })
    );
  }

  // protected getWithBody<T>(path: string, item: unknown, queryOptions: Record<string, unknown> = {}): Observable<T> {
  //   const queryParams = this.objectToQuerystring(queryOptions);
  //   const url = `${this.url}${path ? `/${path}` : ''}${queryParams}`;
  //   console.log(item);
  //   return this.http
  //     .request<T>(RequestMethod.GET, url, {
  //       body: item,
  //       headers: new HttpHeaders({
  //         'Content-Type': 'application/json',
  //       }),
  //     })
  //     .pipe(
  //       tap((response) => {
  //         this.logger({ method: RequestMethod.GET, url }, response);
  //       })
  //     );
  // }

  protected post<T>(path: string, item?: unknown): Observable<T> {
    const url = `${this.url}/${this.endpoint}${path ? `/${path}` : ''}`;
    return this.http.post<T>(url, item).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.POST, url }, response);
      })
    );
  }

  protected delete<T>(path: string, queryOptions: Record<string, unknown> = {}): Observable<T> {
    const queryParams = this.objectToQuerystring(queryOptions);
    const url = `${this.url}/${this.endpoint}${path ? `/${path}` : ''}${queryParams}`;
    return this.http.delete<T>(url).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.DELETE, url }, response);
      })
    );
  }

  protected patch<T>(path: string, item: unknown): Observable<T> {
    const url = `${this.url}/${this.endpoint}${path ? `/${path}` : ''}`;
    return this.http.patch<T>(url, item).pipe(
      tap((response) => {
        this.logger({ method: RequestMethod.PATCH, url }, response);
      })
    );
  }

  /**
   * Transforme un objet en query string
   * @param obj objet
   */
  protected objectToQuerystring(obj: Record<string, unknown>): string {
    if (Object.keys(obj).length === 0) return '';
    return `?${stringify(obj)}`;
  }

  /**
   * Affiche un log en console sur l'environnement local
   * @param req Paramètre de la requête
   * @param response Réponse de la requête
   * @param body Corps de la requête
   */
  protected logger(req: RequestInterface, response: unknown, body: Partial<{ body?: unknown }> = {}): void {
    if (isDevMode()) {
      console.groupCollapsed(`${req.method.toUpperCase()} ${req.url}`);
      if ([RequestMethod.POST, RequestMethod.PUT, RequestMethod.PATCH].includes(req.method)) {
        const bodyItem = body?.body ? body.body : body;
        console.log('Body', bodyItem);
      }
      console.log('Response', response);
      console.groupEnd();
    }
  }
}
